import { useSortableTable } from '../../../hooks/useSortableTable';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AddCustomerModal from '../../../AddCustomerModal';
import { navigateWithPreference } from '../../../utils/navigationHelper';
import { apiFetch } from '../../../utils/api';
import PageHeader from '../../../components/PageHeader';

function Customers() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  
  // Sorting
  const { items: sortedCustomers, requestSort, getSortIndicator } = useSortableTable(customers, { key: 'company_name', direction: 'asc' });
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const columnMenuRef = useRef(null);

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState({
    companyName: true,
    contactName: true,
    phone: true,
    mobile: false,
    email: true,
    address: true,
    city: true,
    county: false,
    postcode: false,
    accountNumber: false,
  });

  // Available columns configuration
  const columns = [
    { key: 'companyName', label: 'Company Name' },
    { key: 'contactName', label: 'Contact Name' },
    { key: 'phone', label: 'Phone' },
    { key: 'mobile', label: 'Mobile' },
    { key: 'email', label: 'Email' },
    { key: 'address', label: 'Address' },
    { key: 'city', label: 'City' },
    { key: 'county', label: 'County' },
    { key: 'postcode', label: 'Postcode' },
    { key: 'accountNumber', label: 'Account #' },
  ];

  useEffect(() => {
    fetchCustomers();
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      const response = await apiFetch('/api/preferences/default/customers');
      const data = await response.json();
      
      if (data.preferences) {
        setVisibleColumns(data.preferences);
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    }
  };

  const savePreferences = async (newPreferences) => {
    try {
      await apiFetch('/api/preferences/default/customers', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preferences: newPreferences })
      });
    } catch (error) {
      console.error('Error saving preferences:', error);
    }
  };

  // Save column visibility to API whenever it changes
  useEffect(() => {
    savePreferences(visibleColumns);
  }, [visibleColumns]);

  // Close column menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (columnMenuRef.current && !columnMenuRef.current.contains(event.target)) {
        setShowColumnMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await apiFetch('/api/customers');
      const data = await response.json();
      setCustomers(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching customers:', error);
      setLoading(false);
    }
  };

  const handleCustomerAdded = () => {
    fetchCustomers();
    setShowAddModal(false);
  };

  const toggleColumn = (columnKey) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  const filteredCustomers = sortedCustomers.filter(customer =>
    customer.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.contact_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.address_line1?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.city?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Count visible columns for colSpan
  const visibleColumnCount = Object.values(visibleColumns).filter(Boolean).length + 1; // +1 for Action column

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <PageHeader 
        title="Customers"
        subtitle={`${customers.length} total`}
        backButton
        onBack={() => navigate('/crm')}
        backTitle="Back to CRM"
        sticky
        actionButton={
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm shadow-sm"
          >
            ➕ Add Customer
          </button>
        }
      />

      <div className="px-4 py-3">
        {/* Search + Column Selector */}
        <div className="mb-3 flex items-center space-x-2">
          <input
            type="text"
            placeholder="Search customers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 max-w-md px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
          
          {/* Column Visibility Button */}
          <div className="relative" ref={columnMenuRef}>
            <button
              onClick={() => setShowColumnMenu(!showColumnMenu)}
              className="px-3 py-1.5 bg-gray-300 hover:bg-gray-400 text-black  rounded text-sm font-medium text-black transition flex items-center space-x-1"
            >
              <span>⚙️</span>
              <span>Columns</span>
            </button>

            {/* Dropdown Menu */}
            {showColumnMenu && (
              <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 w-48">
                <div className="p-2">
                  <div className="text-xs font-semibold text-gray-600 mb-2 px-2">Show Columns</div>
                  {columns.map(column => (
                    <label
                      key={column.key}
                      className="flex items-center px-2 py-1.5 hover:bg-gray-50 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={visibleColumns[column.key]}
                        onChange={() => toggleColumn(column.key)}
                        className="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{column.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded border border-gray-200">
          <div className="overflow-auto max-h-[calc(100vh-240px)] relative">
            <table className="w-full text-sm">
              <thead className="bg-gray-300 sticky top-0 z-20 border-b border-gray-400">
                <tr>
                  {visibleColumns.companyName && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('company_name')}>Company Name{getSortIndicator('company_name')}</th>}
                  {visibleColumns.contactName && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('contact_name')}>Contact Name{getSortIndicator('contact_name')}</th>}
                  {visibleColumns.phone && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('phone')}>Phone{getSortIndicator('phone')}</th>}
                  {visibleColumns.mobile && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('mobile')}>Mobile{getSortIndicator('mobile')}</th>}
                  {visibleColumns.email && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('email')}>Email{getSortIndicator('email')}</th>}
                  {visibleColumns.address && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('address_line1')}>Address{getSortIndicator('address_line1')}</th>}
                  {visibleColumns.city && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('city')}>City{getSortIndicator('city')}</th>}
                  {visibleColumns.county && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('county')}>County{getSortIndicator('county')}</th>}
                  {visibleColumns.postcode && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('postcode')}>Postcode{getSortIndicator('postcode')}</th>}
                  {visibleColumns.accountNumber && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('account_number')}>Account #{ getSortIndicator('account_number')}</th>}
                  <th className="text-right px-3 py-1.5 text-xs font-semibold text-gray-600">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan={visibleColumnCount} className="px-3 py-6 text-center text-gray-500 text-sm">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
                      Loading...
                    </td>
                  </tr>
                ) : filteredCustomers.length === 0 ? (
                  <tr>
                    <td colSpan={visibleColumnCount} className="px-3 py-6 text-center text-gray-500 text-sm">
                      {searchTerm ? 'No matches.' : 'No customers yet.'}
                    </td>
                  </tr>
                ) : (
                  filteredCustomers.map((customer, index) => (
                    <tr
                      key={customer.id}
                      onClick={() => navigateWithPreference(navigate, `/customers/${customer.id}`, 'customers')}
                      className={`hover:bg-blue-200 cursor-pointer transition ${index % 2 === 1 ? 'bg-blue-100' : 'bg-white'}`}
                    >
                      {visibleColumns.companyName && (
                        <td className="px-3 py-2">
                          <div className="font-medium text-gray-900">{customer.company_name}</div>
                        </td>
                      )}
                      {visibleColumns.contactName && <td className="px-3 py-2 text-gray-700">{customer.contact_name || '-'}</td>}
                      {visibleColumns.phone && <td className="px-3 py-2 text-gray-700">{customer.phone || '-'}</td>}
                      {visibleColumns.mobile && <td className="px-3 py-2 text-gray-700">{customer.mobile || '-'}</td>}
                      {visibleColumns.email && <td className="px-3 py-2 text-gray-700">{customer.email || '-'}</td>}
                      {visibleColumns.address && <td className="px-3 py-2 text-gray-700">{customer.address_line1 || '-'}</td>}
                      {visibleColumns.city && <td className="px-3 py-2 text-gray-700">{customer.city || '-'}</td>}
                      {visibleColumns.county && <td className="px-3 py-2 text-gray-700">{customer.county || '-'}</td>}
                      {visibleColumns.postcode && <td className="px-3 py-2 text-gray-700">{customer.postcode || '-'}</td>}
                      {visibleColumns.accountNumber && <td className="px-3 py-2 text-gray-700">{customer.account_number || '-'}</td>}
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/customers/${customer.id}`);
                          }}
                          className="text-blue-600 hover:text-blue-800 text-xs font-medium mr-3"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-2 text-xs text-gray-600">
          Showing {filteredCustomers.length} of {customers.length}
        </div>
      </div>

      {showAddModal && (
        <AddCustomerModal
          onClose={() => setShowAddModal(false)}
          onCustomerAdded={handleCustomerAdded}
        />
      )}
    </div>
  );
}

export default Customers;
